import { mayLeavePage } from "../navigation/leaveGuard";
import type { MouseEvent } from "react";
import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  LinkBox,
  LinkOverlay,
} from "@chakra-ui/react";
import { FiCheck, FiX } from "react-icons/fi";
import { Tooltip } from "../components/Tooltip";
import { useApiError } from "../errors/useApiError";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  type NotificationDto,
  useDismissNotificationsMutation,
  useMarkNotificationsAsViewedMutation,
} from "../../autogen/bff-api";
import { momentLocaleFor } from "../formatting/i18nFormatters";
import { getNotificationDescription } from "./get-notification-description";
import { getNotificationLink } from "./getNotificationLink";
import { usePortalEventUrls } from "../sourcing-events/portal/usePortalEventUrls";
import { CHIP_BG, CHIP_FG, notificationVisual } from "./notificationVisuals";

interface Props {
  notification: NotificationDto;
  selectedOrgId: string;
  onNavigate?: () => void;
  onRead?: (id: string) => void;
  onDismissed?: (id: string) => void;
  onDismissStart?: (id: string) => void;
}

export const NotificationRow = ({
  notification,
  selectedOrgId,
  onNavigate,
  onRead,
  onDismissed,
  onDismissStart,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { trigger } = useApiError();
  const [dismiss, { isLoading: isDismissing }] =
    useDismissNotificationsMutation();
  const momentLocale = momentLocaleFor(i18n.language);
  const [markAsViewed] = useMarkNotificationsAsViewedMutation();
  const visual = notificationVisual(notification.type);
  const portalUrls = usePortalEventUrls();
  const link = getNotificationLink(
    notification,
    selectedOrgId,
    portalUrls.view,
  );
  const unread = !notification.viewed;
  const markViewed = () =>
    markAsViewed({
      markNotificationsRequest: { notificationIds: [notification.id] },
    });
  const open = (event: MouseEvent<HTMLElement>) => {
    if (!mayLeavePage()) {
      event.preventDefault();
      return;
    }
    markViewed();
    onNavigate?.();
  };

  return (
    <LinkBox
      position="relative"
      px="4"
      py="3"
      borderLeft="3px solid"
      borderLeftColor={unread ? "smPrimary" : "transparent"}
      bg="transparent"
      _hover={{ bg: "smBackgroundTertiary" }}
      onClick={link ? undefined : markViewed}
    >
      <Flex gap="3" align="flex-start" pr="16">
        <Flex
          w="34px"
          h="34px"
          rounded="lg"
          flexShrink={0}
          align="center"
          justify="center"
          bg={CHIP_BG[visual.colorScheme]}
        >
          <Icon
            as={visual.icon}
            color={CHIP_FG[visual.colorScheme]}
            boxSize="4"
          />
        </Flex>
        <Box flex="1" minW={0}>
          <Box fontSize="sm" color="smText">
            {link ? (
              <LinkOverlay
                as={RouterLink}
                to={link}
                onClick={open}
              >
                {getNotificationDescription(notification, momentLocale)}
              </LinkOverlay>
            ) : (
              getNotificationDescription(notification, momentLocale)
            )}
          </Box>
          <Box fontSize="xs" mt="0.5" color="smMuted">
            {moment(notification.createdAt).fromNow()}
          </Box>
        </Box>
      </Flex>
      <HStack spacing="0.5" position="absolute" right="6" top="2" zIndex={1}>
        {unread && (
          <Tooltip label={t("Mark as read")}>
            <IconButton
              aria-label={t("Mark as read") ?? ""}
              icon={<Icon as={FiCheck} />}
              size="xs"
              variant="ghost"
              color="smMuted"
              onClick={(e) => {
                e.stopPropagation();
                markAsViewed({
                  markNotificationsRequest: {
                    notificationIds: [notification.id],
                  },
                })
                  .unwrap()
                  .then(() => onRead?.(notification.id))
                  .catch(trigger);
              }}
            />
          </Tooltip>
        )}
        <Tooltip label={t("Dismiss alert")}>
          <IconButton
            aria-label={t("Dismiss alert") ?? ""}
            data-dismiss-alert={notification.id}
            icon={<Icon as={FiX} />}
            size="xs"
            variant="ghost"
            color="smMuted"
            isLoading={isDismissing}
            onClick={(e) => {
              e.stopPropagation();
              onDismissStart?.(notification.id);
              dismiss({
                markNotificationsRequest: {
                  notificationIds: [notification.id],
                },
              })
                .unwrap()
                .then(() => onDismissed?.(notification.id))
                .catch(trigger);
            }}
          />
        </Tooltip>
      </HStack>
      {unread && (
        <Box
          data-testid="notification-unread-dot"
          position="absolute"
          right="3"
          top="50%"
          transform="translateY(-50%)"
          w="2"
          h="2"
          rounded="full"
          bg="smPrimary"
        />
      )}
    </LinkBox>
  );
};
